import React, { ChangeEvent, FC, FormEvent, useEffect, useState } from 'react'
import {
  Alert,
  Box,
  PrimaryButton,
  Grid,
  Heading,
  SecondaryButton,
} from '@moonpig/launchpad-components'
import { gql, useMutation } from '@moonpig/web-core-graphql'
import { TextInput } from '@moonpig/launchpad-forms'
import { Region } from '@moonpig/web-core-types'
import { ErrorAlert, Form } from '@moonpig/web-shared-components'
import { trackGAEvent } from '@moonpig/web-core-analytics'
import {
  ForgottenPasswordMutation as ForgottenPasswordResult,
  ForgottenPasswordMutationVariables,
} from './__generated__/ForgottenPassword'
import { useAccountLocaleText } from '../../text-localisation'

export const ForgottenPasswordGQL = gql`
  mutation ForgottenPassword($input: ForgottenPasswordInput!) {
    forgottenPassword(input: $input) {
      ... on ForgottenPasswordSent {
        __typename
        message
      }
      ... on ForgottenPasswordInvalidEmail {
        __typename
        message
      }
    }
  }
`
const spacing = 7

export const ForgottenPassword: FC<{
  region: Region
  invalidToken: boolean
}> = ({ region, invalidToken }) => {
  const localiseText = useAccountLocaleText()
  const [email, setEmail] = useState('')
  const [emailErrors, setEmailErrors] = useState('')
  const [success, setSuccess] = useState(false)

  const [forgottenPassword, { error: didError, client, loading }] = useMutation<
    ForgottenPasswordResult,
    ForgottenPasswordMutationVariables
  >(ForgottenPasswordGQL, {
    onError: () => {},
    onCompleted: async () => {
      /* istanbul ignore next */
      if (!client) {
        throw new Error('The Apollo client is undefined')
      }

      await client.clearStore()
    },
  })

  const validateForm = (input: string) => {
    if (input === '') {
      setEmailErrors(localiseText('forgotten_password.valid_email_error'))
      return false
    }
    setEmailErrors('')
    return true
  }

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    trackGAEvent({
      event: 'select_content',
      content_data: {
        content_type: 'account | forgotten password | send email submitted',
      },
      event_data: {
        action: 'account | forgotten password | send email submitted',
        category: 'account',
        label: 'send email submitted',
      },
    })
    if (validateForm(email)) {
      const response = await forgottenPassword({
        variables: { input: { email } },
      })

      if (response && response.data && response.data.forgottenPassword) {
        const result = response.data.forgottenPassword
        switch (result.__typename) {
          case 'ForgottenPasswordSent':
            setSuccess(true)
            trackGAEvent({
              event: 'impression',
              content_data: {
                content_type:
                  'account | forgotten password | send email success',
              },
              event_data: {
                action: 'account | forgotten password | send email success',
                category: 'account',
                label: 'send email success',
              },
            })
            break
          case 'ForgottenPasswordInvalidEmail':
            setEmailErrors(result.message)
            trackGAEvent({
              event: 'generic_error',
              content_data: {
                content_type: 'account | forgotten password | send email error',
              },
              event_data: {
                action: 'account | forgotten password | send email error',
                category: 'account',
                label: 'send email error',
              },
              error_data: {
                id: 'ForgottenPasswordInvalidEmail',
                message: result.message,
              },
            })
            break
          /* istanbul ignore next*/
          default:
            break
        }
      }
    }
  }

  const handleCancel = () => {
    window.location.assign(`/${region}/account/login`)
  }

  const emailOnChange = (e: ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value)
    validateForm(e.target.value)
  }

  useEffect(() => {
    if (invalidToken) {
      trackGAEvent({
        event: 'generic_error',
        content_data: {
          content_type:
            'account | forgotten password | new password submit error',
        },
        event_data: {
          action: 'account | forgotten password | new password submit error',
          category: 'account',
          label: 'new password submit error',
        },
        error_data: {
          id: 'ForgottenPasswordInvalidEmail',
          message: localiseText(
            'forgotten_password.link_expired_error_message',
          ),
        },
      })
    }
  })

  return (
    <Form onSubmit={onSubmit} method="post">
      <Box width={{ xs: 1, md: 3 / 4, xl: 2 / 5 }} mx="auto" my={spacing}>
        {!success && (
          <>
            <Heading level="h2" as="h1" mb={spacing} textAlign={'center'}>
              {!invalidToken
                ? localiseText('forgotten_password.heading')
                : localiseText('forgotten_password.invalid_token_error')}
            </Heading>
            {!invalidToken && (
              <Box my={spacing} textAlign={'center'}>
                {localiseText('forgotten_password.instructions')}
              </Box>
            )}
            {invalidToken && (
              <ErrorAlert
                title={localiseText(
                  'forgotten_password.link_expired_error_title',
                )}
                message={localiseText(
                  'forgotten_password.link_expired_error_message',
                )}
              />
            )}

            <TextInput
              label={localiseText('forgotten_password.email_input_label')}
              name="email"
              type="email"
              placeholder={localiseText(
                'forgotten_password.email_input_placeholder',
              )}
              value={email}
              onChange={emailOnChange}
              mb={spacing}
              error={!!emailErrors}
              helperText={emailErrors}
            />

            <Grid gap>
              <Box order={{ xs: 1, sm: 2 }} width={{ xs: 1, sm: 1 / 2 }}>
                <PrimaryButton type="submit" width="100%" disabled={loading}>
                  {localiseText('forgotten_password.send')}
                </PrimaryButton>
              </Box>
              <Box order={{ xs: 2, sm: 1 }} width={{ xs: 1, sm: 1 / 2 }}>
                <SecondaryButton
                  type="button"
                  width="100%"
                  onClick={handleCancel}
                >
                  {localiseText('forgotten_password.cancel')}
                </SecondaryButton>
              </Box>
            </Grid>
          </>
        )}
        {success && (
          <Box>
            <Heading level="h2" as="h1">
              {localiseText('forgotten_password.email_sent')}
            </Heading>
            {localiseText('forgotten_password.success', email)}
          </Box>
        )}
        {didError && (
          <Box>
            <Alert variant="error" my={5}>
              {localiseText('forgotten_password.failure')}
            </Alert>
          </Box>
        )}
      </Box>
    </Form>
  )
}
